import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  ECommerceAdsState,
  Advertisement,
  AdPlacement,
  QueryAdsDto,
  GetAdsByOrderRangeDto,
  QueryPlacementDto,
  AdsResponse,
  PlacementsResponse,
  StatusAdsQueryDto,
  StatusAdsResponse,
} from './types';

// Base URL for the API
const API_BASE_URL  = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX) {
  console.warn("Warning: REACT_APP_ADVERTISEMENTS_API_PERFIX is not defined.");
}
// Initial state
const initialState: ECommerceAdsState = {
  ads: [],
  activeAds: [],
  adsByPlacement: [],
  adsByOrderRange: [],
  statusAds: [],
  placements: [],
  pagination: null,
  activeAdsPagination: null,
  statusAdsPagination: null,
  adsByPlacementPagination: null,
  adsByOrderRangePagination: null,
  placementsPagination: null,
  selectedStatusAd: null,
  loading: {
    ads: false,
    activeAds: false,
    adsByPlacement: false,
    adsByOrderRange: false,
    placements: false,
    statusAds: false,
  },
  error: null,
};

// Async thunks for API calls

/**
 * Fetch all ads with filters
 */
export const fetchAds = createAsyncThunk<
  AdsResponse,
  QueryAdsDto,
  { rejectValue: string }
>(
  'ecommerceAds/fetchAds',
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<AdsResponse>(
        `${API_BASE_URL}/ecommerce/ads`,
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ads'
      );
    }
  }
);

/**
 * Fetch all active ads with pagination
 */
export const fetchActiveAds = createAsyncThunk<
  AdsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>(
  'ecommerceAds/fetchActiveAds',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get<AdsResponse>(
        `${API_BASE_URL}/ecommerce/ads/active`,
        { params: { page, limit } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch active ads'
      );
    }
  }
);

/**
 * Fetch ads by placement ID
 */
export const fetchAdsByPlacementId = createAsyncThunk<
  AdsResponse,
  { placementId: string; page?: number; limit?: number },
  { rejectValue: string }
>(
  'ecommerceAds/fetchAdsByPlacementId',
  async ({ placementId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await axios.get<AdsResponse>(
        `${API_BASE_URL}/ecommerce/ads/placement/${placementId}`,
        { params: { page, limit } }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ads by placement'
      );
    }
  }
);

/**
 * Fetch ads by advertisement order range
 */
export const fetchAdsByOrderRange = createAsyncThunk<
  AdsResponse,
  GetAdsByOrderRangeDto,
  { rejectValue: string }
>(
  'ecommerceAds/fetchAdsByOrderRange',
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<AdsResponse>(
        `${API_BASE_URL}/ecommerce/ads/order-range`,
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ads by order range'
      );
    }
  }
);

/**
 * Fetch all placements with filters and pagination
 */
export const fetchPlacements = createAsyncThunk<
  PlacementsResponse,
  QueryPlacementDto,
  { rejectValue: string }
>(
  'ecommerceAds/fetchPlacements',
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await axios.get<PlacementsResponse>(
        `${API_BASE_URL}/ecommerce/ads/placements`,
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch placements'
      );
    }
  }
);


/**
 * Fetch all status ads with pagination
 */
export const fetchStatusAds = createAsyncThunk<
  StatusAdsResponse,
  StatusAdsQueryDto,
  { rejectValue: string }
>(
  'ecommerceAds/fetchStatusAds',
  async (queryParams, { rejectWithValue }) => {   
    try {
      const response = await axios.get<StatusAdsResponse>(
        `${API_BASE_URL}/ecommerce/ads/status`,
        { params: queryParams }
      );
      console.log("fetchStatusAds", response.data.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch status ads'
      );
    }
  }
);

// Slice
const eCommerceAdsSlice = createSlice({
  name: 'eCommerceAds',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAds: (state) => {
      state.ads = [];
      state.pagination = null;
    },
    clearActiveAds: (state) => {
      state.activeAds = [];
      state.activeAdsPagination = null;
    },
    clearStatusAds: (state) => {
      state.statusAds = [];
      state.statusAdsPagination = null;
    },
    clearAdsByPlacement: (state) => {
      state.adsByPlacement = [];
      state.adsByPlacementPagination = null;
    },
    clearAdsByOrderRange: (state) => {
      state.adsByOrderRange = [];
      state.adsByOrderRangePagination = null;
    },
    setSelectedStatusAd: (state, action: PayloadAction<Advertisement | null>) => {
      state.selectedStatusAd = action.payload;
    },
    clearSelectedStatusAd: (state) => {
      state.selectedStatusAd = null;
    },
    clearPlacements: (state) => {
      state.placements = [];
      state.placementsPagination = null;
    },
    resetECommerceAdsState: () => initialState,

  },
  extraReducers: (builder) => {
    // Fetch Ads
    builder
      .addCase(fetchAds.pending, (state) => {
        state.loading.ads = true;
        state.error = null;
      })
      .addCase(fetchAds.fulfilled, (state, action: PayloadAction<AdsResponse>) => {
        state.loading.ads = false;
        state.ads = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchAds.rejected, (state, action) => {
        state.loading.ads = false;
        // state.error = action.payload || 'Failed to fetch ads';
      });

    // Fetch Active Ads
    builder
      .addCase(fetchActiveAds.pending, (state) => {
        state.loading.activeAds = true;
        state.error = null;
      })
      .addCase(fetchActiveAds.fulfilled, (state, action: PayloadAction<AdsResponse>) => {
        state.loading.activeAds = false;
        state.activeAds = action.payload.data;
        state.activeAdsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchActiveAds.rejected, (state, action) => {
        state.loading.activeAds = false;
        // state.error = action.payload || 'Failed to fetch active ads';
      });

    // Fetch Ads By Placement ID
    builder
      .addCase(fetchAdsByPlacementId.pending, (state) => {
        state.loading.adsByPlacement = true;
        state.error = null;
      })
      .addCase(fetchAdsByPlacementId.fulfilled, (state, action: PayloadAction<AdsResponse>) => {
        state.loading.adsByPlacement = false;
        state.adsByPlacement = action.payload.data;
        state.adsByPlacementPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchAdsByPlacementId.rejected, (state, action) => {
        state.loading.adsByPlacement = false;
        // state.error = action.payload || 'Failed to fetch ads by placement';
      });

    // Fetch Ads By Order Range
    builder
      .addCase(fetchAdsByOrderRange.pending, (state) => {
        state.loading.adsByOrderRange = true;
        state.error = null;
      })
      .addCase(fetchAdsByOrderRange.fulfilled, (state, action: PayloadAction<AdsResponse>) => {
        state.loading.adsByOrderRange = false;
        state.adsByOrderRange = action.payload.data;
        state.adsByOrderRangePagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchAdsByOrderRange.rejected, (state, action) => {
        state.loading.adsByOrderRange = false;
        // state.error = action.payload || 'Failed to fetch ads by order range';
      });

    // Fetch Placements
    builder
      .addCase(fetchPlacements.pending, (state) => {
        state.loading.placements = true;
        state.error = null;
      })
      .addCase(fetchPlacements.fulfilled, (state, action: PayloadAction<PlacementsResponse>) => {
        state.loading.placements = false;
        state.placements = action.payload.data;
        state.placementsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchPlacements.rejected, (state, action) => {
        state.loading.placements = false;
        // state.error = action.payload || 'Failed to fetch placements';
      });
    // Fetch Status Ads
    builder
      .addCase(fetchStatusAds.pending, (state) => {
        state.loading.statusAds = true;
        state.error = null;
      })
      .addCase(fetchStatusAds.fulfilled, (state, action: PayloadAction<StatusAdsResponse>) => {
        state.loading.statusAds = false;
        state.statusAds = action.payload.data;
        state.statusAdsPagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchStatusAds.rejected, (state, action) => {
        state.loading.statusAds = false;
        // state.error = action.payload || 'Failed to fetch status ads';
      });
  },
});

export const {
  clearError,
  clearAds,
  clearActiveAds,
  clearAdsByPlacement,
  clearAdsByOrderRange,
  clearPlacements,
  resetECommerceAdsState,
  clearStatusAds,
  setSelectedStatusAd,
  clearSelectedStatusAd,
} = eCommerceAdsSlice.actions;

export default eCommerceAdsSlice.reducer;

